import React from 'react';

import {
  Stack,
  IconButton,
  IndeterminateCheckBoxIcon,
  AddBoxIcon,
  Typography,
} from '@gogame/components';
import DataGrid from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenTitle,
  useScreenDispatch,
  useScreenState,
} from '@gogame/screens';

import {
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/MainBudgetPlanning/screenReducer';
import MainBudgetPlanningActions from 'scenes/MainBudgetPlanning/components/Actions';
import { MainBudgetPlanningFormEdit } from 'scenes/MainBudgetPlanning/components/MainBudgetPlanningForm';
import {
  COLUMN_PERIOD,
  COLUMN_APPLICATION,
  COLUMN_TOTAL_SPEND,
  COLUMN_LAST_UPDATED,
  COLUMN_CREATED_BY,
  COLUMN_CREATED,
} from 'scenes/MainBudgetPlanning/constants';
import { useLoadMainBudgetPlanningTable } from 'scenes/MainBudgetPlanning/data';

import Toolbar from './Toolbar';

const getRowId = ({ path }) => path;

const getTreeDataPath = ({ path }) => path.split('/');

const MainBudgetPlanningDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { tableSettings } = screenState;

  const { data, isLoading, isRefetching } =
    useLoadMainBudgetPlanningTable(tableSettings);
  const [childComponent, openChildComponent] = useChildComponent();

  const handleEditAction = ({ id }) => {
    openChildComponent(MainBudgetPlanningFormEdit, {
      id,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        storageKey={'mainBudgetPlanning'}
        defaultColumnWidth={120}
        getRowId={getRowId}
        treeData={true}
        getTreeDataPath={getTreeDataPath}
        hideGroupingColumn={true}
        notInitedLabel={'No records'}
      >
        <DataGrid.Column
          field={COLUMN_PERIOD}
          type={'dateRange'}
          headerName={'Period'}
          template={'MONTH'}
          placeholder={''}
        />

        <DataGrid.Column
          headerName={'Applications'}
          field={COLUMN_APPLICATION}
          sortable={false}
          width={240}
          renderCell={({ rowNode, row, api }) => {
            const childrenLength = rowNode.children?.length;

            const withNesting = childrenLength > 1;

            const childrenNames = rowNode.children?.map(
              (id) => api.getRow(id)?.application?.name
            );

            return (
              <Stack
                direction={'row'}
                alignItems={'center'}
                pl={(!withNesting ? 4.5 : 0) + (rowNode.depth ? 1.5 : 0)}
                ml={-1}
                height={'100%'}
              >
                {withNesting ? (
                  <IconButton
                    onClick={() =>
                      api.setRowChildrenExpansion(
                        rowNode.id,
                        !rowNode.childrenExpanded
                      )
                    }
                    icon={
                      rowNode.childrenExpanded ? (
                        <IndeterminateCheckBoxIcon fontSize={'small'} />
                      ) : (
                        <AddBoxIcon fontSize={'small'} />
                      )
                    }
                    tooltip={rowNode.childrenExpanded ? 'Collapse' : 'Expand'}
                  />
                ) : null}
                <Typography variant={'body2'}>
                  {withNesting
                    ? `${childrenLength} applications`
                    : childrenNames?.[0] || row.application?.name}
                </Typography>
              </Stack>
            );
          }}
        />

        <DataGrid.Column
          headerName={'Total Spend'}
          field={COLUMN_TOTAL_SPEND}
          width={130}
          type={'currency'}
          getValue={(v) => Number(v?.toFixed(2))}
        />

        <DataGrid.Column
          field={COLUMN_LAST_UPDATED}
          headerName={'Last Updated'}
          width={135}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          placeholder={''}
        />

        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={150}
          type={'text'}
          getValue={(v) => v?.name}
          placeholder={''}
        />

        <DataGrid.Column
          field={COLUMN_CREATED}
          headerName={'Created'}
          width={135}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          placeholder={''}
        />

        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={MainBudgetPlanningActions}
          onEdit={handleEditAction}
          limit={2}
          isActionVisible={({ rowNode }) => rowNode.children?.length}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default MainBudgetPlanningDataTable;
